import { useEffect, useState, useCallback } from 'react';
import { fetchNotifications, refreshEmailStatus } from '../pages/services/notificationService';
import { TypeBadge, formatDateTime, statusSummary, thStyle, tdStyle } from './notificationDisplay';
import NotificationDetailsDialog from './NotificationDetailsDialog';

const NotificationRow = ({ notif, onRefresh, onOpenDetails }) => {
  const [refreshing, setRefreshing] = useState(false);

  const handleRefresh = async (e) => {
    e.stopPropagation();
    setRefreshing(true);
    try {
      await onRefresh(notif._id);
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <tr
      style={{ borderBottom: '1px solid #f3f4f6', cursor: 'pointer' }}
      onClick={() => onOpenDetails(notif)}
    >
      <td style={tdStyle}>
        <span style={{ color: '#6b7280', fontSize: 12, whiteSpace: 'nowrap' }}>
          {formatDateTime(notif.sentAt)}
        </span>
      </td>
      <td style={tdStyle}><TypeBadge type={notif.type} /></td>
      <td style={{ ...tdStyle, maxWidth: 240 }}>
        <span style={{ fontWeight: 500, color: '#111827', fontSize: 13, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {notif.subject || '—'}
        </span>
        {notif.bodyPreview && (
          <span style={{ color: '#6b7280', fontSize: 12, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {notif.bodyPreview}
          </span>
        )}
      </td>
      <td style={{ ...tdStyle, textAlign: 'center' }}>
        <span style={{ fontWeight: 600, color: '#059669' }}>{notif.sentCount}</span>
        {notif.failedCount > 0 && (
          <span style={{ fontWeight: 600, color: '#dc2626', marginLeft: 4 }}>/ {notif.failedCount} failed</span>
        )}
      </td>
      <td style={tdStyle}>
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>{statusSummary(notif)}</div>
      </td>
      <td style={{ ...tdStyle, textAlign: 'right' }}>
        {notif.type === 'email' && (
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            style={{
              padding: '4px 10px',
              fontSize: 12,
              borderRadius: 6,
              border: '1px solid #d1d5db',
              background: '#fff',
              cursor: refreshing ? 'not-allowed' : 'pointer',
              color: '#374151',
              fontWeight: 500,
              opacity: refreshing ? 0.6 : 1,
              whiteSpace: 'nowrap',
            }}
          >
            {refreshing ? 'Refreshing…' : '↻ Status'}
          </button>
        )}
      </td>
    </tr>
  );
};

const NotificationHistorySection = ({ entityType, entityId }) => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const PAGE_SIZE = 10;

  const load = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const res = await fetchNotifications(entityType, entityId, p, PAGE_SIZE);
      setNotifications(res.data || []);
      setTotal(res.pagination?.total || 0);
    } catch (err) {
      console.error('Failed to load notifications:', err);
    } finally {
      setLoading(false);
    }
  }, [entityType, entityId]);

  useEffect(() => {
    if (entityId) load(page);
  }, [entityId, page, load]);

  const handleRefresh = async (notifId) => {
    try {
      const res = await refreshEmailStatus(notifId);
      setNotifications((prev) => prev.map((n) => (n._id === notifId ? res.data : n)));
    } catch (err) {
      alert(err.message || 'Failed to refresh status');
    }
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div className="bd-card">
      <p className="bd-card-heading">Notification History ({total})</p>

      {loading ? (
        <div className="bd-students-loading">
          <div className="bd-students-spinner" />
          <span style={{ marginLeft: 10, color: '#6b7280', fontSize: 14 }}>Loading notifications…</span>
        </div>
      ) : notifications.length === 0 ? (
        <div className="bd-empty-state">
          <span className="bd-empty-icon">🔔</span>
          <p>No notifications sent yet</p>
        </div>
      ) : (
        <>
          <div className="bd-table-wrapper">
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#f9fafb' }}>
                  <th style={thStyle}>Sent At</th>
                  <th style={thStyle}>Type</th>
                  <th style={thStyle}>Subject / Template</th>
                  <th style={{ ...thStyle, textAlign: 'center' }}>Recipients</th>
                  <th style={thStyle}>Status Summary</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {notifications.map((notif) => (
                  <NotificationRow key={notif._id} notif={notif} onRefresh={handleRefresh} onOpenDetails={setSelectedNotification} />
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 16 }}>
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                style={{ padding: '5px 14px', borderRadius: 6, border: '1px solid #d1d5db', background: '#fff', cursor: page === 1 ? 'not-allowed' : 'pointer', opacity: page === 1 ? 0.5 : 1 }}
              >
                ‹ Prev
              </button>
              <span style={{ lineHeight: '32px', fontSize: 13, color: '#6b7280' }}>
                {page} / {totalPages}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                style={{ padding: '5px 14px', borderRadius: 6, border: '1px solid #d1d5db', background: '#fff', cursor: page === totalPages ? 'not-allowed' : 'pointer', opacity: page === totalPages ? 0.5 : 1 }}
              >
                Next ›
              </button>
            </div>
          )}
        </>
      )}

      <NotificationDetailsDialog
        notification={selectedNotification}
        isOpen={!!selectedNotification}
        onClose={() => setSelectedNotification(null)}
        onResendSuccess={() => load(page)}
      />
    </div>
  );
};

export default NotificationHistorySection;
